class AnimatedText extends HTMLElement {
  connectedCallback() {
    this._wrapWords();
    this._resizeObserver = new ResizeObserver(() => this._updateRanges());
    this._resizeObserver.observe(document.documentElement);
    requestAnimationFrame(() => this._updateRanges());
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
  }

  _wrapWords() {
    const tokens = this._tokenize(this.childNodes);
    this.innerHTML = '';

    let word = null;

    const flush = () => {
      if (word) {
        this.appendChild(word);
        word = null;
      }
    };

    for (const token of tokens) {
      if (token.type === 'space') {
        flush();
        this.appendChild(document.createTextNode(' '));
      } else if (token.type === 'break') {
        flush();
        this.appendChild(token.node.cloneNode(false));
      } else {
        if (!word) {
          word = document.createElement('span');
          word.className = 'animated-text__word';
        }
        if (token.node) {
          word.appendChild(token.node.cloneNode(true));
        } else {
          word.appendChild(document.createTextNode(token.text));
        }
      }
    }

    flush();
  }

  _tokenize(nodeList) {
    const tokens = [];
    for (const node of nodeList) {
      if (node.nodeType === Node.TEXT_NODE) {
        for (const part of node.textContent.split(/(\s+)/)) {
          if (!part) continue;
          if (/^\s+$/.test(part)) {
            tokens.push({ type: 'space' });
          } else {
            tokens.push({ type: 'word-part', text: part });
          }
        }
      } else if (node.nodeName === 'BR') {
        tokens.push({ type: 'break', node });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Elements containing spaces are recursed into (losing the wrapper);
        // single-word elements like <strong> or <sup> are kept whole.
        if (/\s/.test(node.textContent)) {
          tokens.push(...this._tokenize(node.childNodes));
        } else {
          tokens.push({ type: 'word-part', node });
        }
      }
    }
    return tokens;
  }

  _updateRanges() {
    const words = this.querySelectorAll('.animated-text__word');
    if (!words.length) return;

    const rect = this.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    const style = getComputedStyle(this);
    const animateStart = parseFloat(style.getPropertyValue('--animate-start')) || 0.2;
    const animateEnd = parseFloat(style.getPropertyValue('--animate-end')) || 0.5;

    const vh = window.innerHeight;
    const elTop = rect.top + window.scrollY;

    // Scroll position where element top reaches animateStart% from bottom of viewport
    const rangeStart = Math.max(0, elTop - vh * (1 - animateStart));
    // Scroll position where element top reaches animateEnd% from bottom of viewport
    const rangeEnd = Math.max(0, elTop - vh * (1 - animateEnd));
    const step = (rangeEnd - rangeStart) / words.length;

    words.forEach((word, i) => {
      word.style.setProperty('animation-range-start', `${rangeStart + i * step}px`);
      word.style.setProperty('animation-range-end', `${rangeStart + (i + 1) * step}px`);
    });
  }
}

if (!customElements.get('animated-text')) {
  customElements.define('animated-text', AnimatedText);
}
